/* eslint-disable max-lines */
import { SourceType } from "../../model/enums/SDKSourceType";
import { TokenType, tokenTypeIsPure, } from "../../model/enums/SDKTokenType";
import { ThemeUtilities } from "../../model/themes/SDKThemeUtilities";
import { TokenTheme, } from "../../model/themes/SDKTokenTheme";
import { BlurToken } from "../../model/tokens/SDKBlurToken";
import { BorderToken } from "../../model/tokens/SDKBorderToken";
import { ColorToken } from "../../model/tokens/SDKColorToken";
import { BorderWidthToken, DimensionToken, DurationToken, FontSizeToken, LetterSpacingToken, LineHeightToken, OpacityToken, ParagraphSpacingToken, RadiusToken, SizeToken, SpaceToken, ZIndexToken, } from "../../model/tokens/SDKDimensionToken";
import { GradientToken } from "../../model/tokens/SDKGradientToken";
import { ShadowToken } from "../../model/tokens/SDKShadowToken";
import { FontFamilyToken, FontWeightToken, ProductCopyToken, StringToken, } from "../../model/tokens/SDKStringToken";
import { TextCaseToken } from "../../model/tokens/SDKTextCaseToken";
import { TextDecorationToken } from "../../model/tokens/SDKTextDecorationToken";
import { TypographyToken } from "../../model/tokens/SDKTypographyToken";
import { VisibilityToken } from "../../model/tokens/SDKVisibilityToken";
import { sortUsingMap, sureOf } from "../../utils/CommonUtils";
import { UnreachableCaseError } from "../../utils/UnreachableCaseError";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Function Definition
export class TokenResolver {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(designSystemId, versionId) {
        // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
        // MARK: - Configuration
        this.hashedTokens = new Map();
        this.resolvedTokens = new Map();
        this.hashedOverrides = new Map();
        this.hashedReconstructedOverrides = new Map();
        this.resolvedOverrides = new Map();
        this.designSystemId = designSystemId;
        this.versionId = versionId;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Resolution
    resolveTokenData(data, tokenGroups, elementProperties, elementViews, elementValues, sources) {
        // Core view always present
        const firstView = sureOf(elementViews.filter((v) => v.isDefault)[0]);
        const indexes = new Map();
        for (const column of firstView.columns) {
            if (column.propertyDefinitionId) {
                indexes.set(column.propertyDefinitionId, firstView.columns.indexOf(column));
            }
        }
        const sortedElementProperties = sortUsingMap(elementProperties, indexes, (p) => p.id);
        data.forEach((token) => {
            this.hashedTokens.set(token.persistentId, token);
        });
        /*
         * Step 1: Create pure tokens that are not references
         */
        for (const rawToken of data) {
            // Skip creation of all tokens that can have reference
            if (rawToken.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            // Construct raw colors, fonts, texts, radii and dimensions first
            if (tokenTypeIsPure(rawToken.type)) {
                const token = this.constructValueToken(rawToken, sortedElementProperties, elementValues);
                this.resolvedTokens.set(token.id, token);
            }
        }
        /*
         * Step 2: Create pure tokens that are references. This will provide us with all possible tokens resolved for mixins later
         */
        for (const rawToken of data) {
            // This time, skip creation of all raw tokens because we already have them
            if (!rawToken.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            // Construct references for pure tokens, if any
            if (tokenTypeIsPure(rawToken.type) &&
                !this.resolvedTokens.get(rawToken.persistentId)) {
                const token = this.constructReferencedToken(rawToken, sortedElementProperties, elementValues);
                this.resolvedTokens.set(token.id, token);
            }
        }
        /*
         * Step 3: Create color tokens that can potentially reference pure resolved tokens
         */
        for (const rawToken of data) {
            // Skip creation of all tokens that can have reference
            if (rawToken.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            // Construct colors
            if (rawToken.type === TokenType.color) {
                const token = this.constructValueToken(rawToken, sortedElementProperties, elementValues);
                this.resolvedTokens.set(token.id, token);
            }
        }
        /*
         * Step 4: Create all remaining color tokens
         */
        for (const rawToken of data) {
            // Skip creation of all tokens that are not references
            if (!rawToken.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            if (rawToken.type === TokenType.color) {
                const token = this.constructReferencedToken(rawToken, sortedElementProperties, elementValues);
                this.resolvedTokens.set(token.id, token);
            }
        }
        /*
         * Step 5: Create mixin tokens that can potentially reference pure resolved tokens
         */
        for (const rawToken of data) {
            // Skip creation of all tokens that can have reference
            if (rawToken.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            // Construct raw typography, gradient, shadow and border colors
            if (!tokenTypeIsPure(rawToken.type) &&
                rawToken.type !== TokenType.color) {
                const token = this.constructValueToken(rawToken, sortedElementProperties, elementValues);
                this.resolvedTokens.set(token.id, token);
            }
        }
        /*
         * Step 6: Create all remaining tokens, as all pure tokens that can be references and all value tokens with possible mixins were all resolved.
         */
        for (const rawToken of data) {
            // Skip creation of all tokens that are not references
            if (!rawToken.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            if (!this.resolvedTokens.get(rawToken.persistentId)) {
                // We create the token only when it wasn't created already. In some cases, this can happen when there are multiple reference levels
                const token = this.constructReferencedToken(rawToken, sortedElementProperties, elementValues);
                this.resolvedTokens.set(token.id, token);
            }
        }
        /*
         * Step 7: Assign parents to the tree
         */
        for (const tokenGroup of tokenGroups) {
            for (const childId of tokenGroup.childrenIds) {
                const possibleToken = this.resolvedTokens.get(childId);
                if (possibleToken) {
                    // If object exists, assign parent to it. Not existing means it is a group
                    possibleToken.parentGroupId = tokenGroup.id;
                    possibleToken.setTokenPath([...tokenGroup.path, tokenGroup.name]);
                }
            }
        }
        // Retrieve all properly resolved tokens
        const tokens = Array.from(this.resolvedTokens.values());
        // Temporary: Fix shadow, gradient and blur tokens
        this.fixMultilayerShadowTokens(tokens.filter((t) => t.tokenType === TokenType.shadow), tokenGroups);
        this.fixMultilayerGradientTokens(tokens.filter((t) => t.tokenType === TokenType.gradient), tokenGroups);
        this.fixMultilayerBlurTokens(tokens.filter((t) => t.tokenType === TokenType.blur), tokenGroups);
        // Assign sourceType to origin to differenciate Figma from Tokens Studio tokens
        for (const token of tokens.filter((t) => { var _a; return ((_a = t.origin) === null || _a === void 0 ? void 0 : _a.sourceId) && !t.origin.sourceType; })) {
            // @ts-expect-error TS(2531): Object is possibly 'null'.
            const source = sources.find((s) => s.id === token.origin.sourceId);
            if (source) {
                // @ts-expect-error TS(2531): Object is possibly 'null'.
                token.origin.sourceType = source.type;
            }
        }
        // Retrieve all properly resolved tokens
        return tokens;
    }
    constructStringValueForReplica(type, replica, override) {
        switch (type) {
            case TokenType.string:
                ;
                replica.value = this.constructTextLikeTokenValue(
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.productCopy:
                ;
                replica.value = this.constructTextLikeTokenValue(
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.fontFamily:
                ;
                replica.value = this.constructTextLikeTokenValue(
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.fontWeight:
                ;
                replica.value = this.constructTextLikeTokenValue(
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                override.data.value);
                break;
            default:
                throw new UnreachableCaseError(type);
        }
    }
    constructOptionValueForReplica(type, replica, override) {
        switch (type) {
            case TokenType.textCase:
                ;
                replica.value = this.constructOptionLikeTokenValue(
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.textDecoration:
                ;
                replica.value =
                    this.constructOptionLikeTokenValue(
                    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                    override.data.value);
                break;
            case TokenType.visibility:
                ;
                replica.value =
                    this.constructOptionLikeTokenValue(
                    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                    override.data.value);
                break;
            default:
                throw new UnreachableCaseError(type);
        }
    }
    constructDimensionValueForReplica(type, replica, override) {
        switch (type) {
            case TokenType.dimension:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.size:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.space:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.opacity:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.fontSize:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.lineHeight:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.letterSpacing:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.paragraphSpacing:
                ;
                replica.value =
                    this.constructDimensionValue(
                    // @ts-expect-error TS(2769): No overload matches this call.
                    override.data.value, type);
                break;
            case TokenType.borderWidth:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.radius:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.duration:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            case TokenType.zIndex:
                ;
                replica.value = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                override.data.value, type);
                break;
            default:
                throw new UnreachableCaseError(type);
        }
    }
    constructPureValueForReplica(type, replica, override) {
        switch (type) {
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                this.constructDimensionValueForReplica(type, replica, override);
                break;
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            case TokenType.fontWeight:
                this.constructStringValueForReplica(type, replica, override);
                break;
            case TokenType.textCase:
            case TokenType.textDecoration:
            case TokenType.visibility:
                this.constructOptionValueForReplica(type, replica, override);
                break;
            default:
                throw new UnreachableCaseError(type);
        }
    }
    resolveThemeData(data, tokens, tokenGroups, sources) {
        const themeId = data.id;
        // Build cache for performance
        for (const token of tokens) {
            this.resolvedTokens.set(token.id, token);
        }
        /*
         * Step 0: Clean overrides that don't have any token associated with it
         */
        const overrideData = [];
        for (const override of data.overrides) {
            const token = this.resolvedTokens.get(override.tokenPersistentId);
            if (token) {
                overrideData.push(override);
            }
        }
        for (const override of overrideData) {
            this.hashedOverrides.set(override.tokenPersistentId, override);
        }
        /*
         * Step 1: Construct containers for tokens that have overrides - but don't assign value to them just yet
         */
        for (const override of overrideData) {
            const token = this.resolvedTokens.get(override.tokenPersistentId);
            const { origin } = override;
            if (!token) {
                throw new Error(
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                `Unable to resolve token ${override.tokenPersistentId} for theme ${data.id} as base token was not found`);
            }
            const replica = this.makeThemedValuelessTokenReplica(token, 
            // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
            themeId, origin);
            this.hashedReconstructedOverrides.set(replica.id, replica);
        }
        /*
         * Step 2: Create map with all tokens, using core tokens and the theme itself so we have all tokens prepared
         */
        for (const override of overrideData) {
            // Skip creation of all tokens that can have reference
            if (override.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            // Construct raw fonts, texts, radii and dimensions first
            if (tokenTypeIsPure(override.type)) {
                if (override.data.value === undefined || override.data.value === null) {
                    throw new Error(`Override must always have alias or value, but ${override.tokenPersistentId} provided neither (1: raw)`);
                }
                // There is no reference to be solved, so we just assume we can create referenced without issues
                const replica = this.hashedReconstructedOverrides.get(override.tokenPersistentId);
                // @ts-expect-error TS(2345): Argument of type 'Token | undefined' is not assign... Remove this comment to see the full error message
                this.constructPureValueForReplica(override.type, replica, override);
                // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                this.resolvedOverrides.set(replica.id, replica);
            }
        }
        /*
         * Step 3: Create values for theme overrides that are raw and pure tokens
         */
        for (const override of overrideData) {
            // This time, skip creation of all raw tokens because we already have them
            if (!override.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            // Construct references for pure tokens, if any
            if (tokenTypeIsPure(override.type) &&
                !this.resolvedOverrides.get(override.tokenPersistentId)) {
                const resolvedOverride = this.resolvedOverrides.get(override.tokenPersistentId);
                if (!resolvedOverride) {
                    // Only construct reference if it wasn't constructed already
                    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                    const replica = this.constructReferencedThemedToken(override, themeId);
                    this.resolvedOverrides.set(replica.id, replica);
                }
            }
        }
        /*
         * Step 4: Create values for theme overrides that are color tokens
         */
        for (const override of overrideData) {
            // Skip creation of all tokens that can have reference in this step
            if (override.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            if (override.type === TokenType.color) {
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                const token = this.constructThemedValueToken(override, themeId);
                this.resolvedOverrides.set(token.id, token);
            }
        }
        /*
         * Step 5: Create color tokens that are references. End of this step will provide us with all possible tokens resolved for mixins later
         */
        for (const override of overrideData) {
            // Skip creation of all tokens that are not references
            if (!override.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            if (override.type === TokenType.color &&
                !this.resolvedOverrides.get(override.tokenPersistentId)) {
                const resolvedOverride = this.resolvedOverrides.get(override.tokenPersistentId);
                if (!resolvedOverride) {
                    // Only construct reference if it wasn't constructed already
                    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                    const replica = this.constructReferencedThemedToken(override, themeId);
                    this.resolvedOverrides.set(replica.id, replica);
                }
            }
        }
        /*
         * Step 6: Create pure tokens that are references.
         */
        for (const override of overrideData) {
            // Skip creation of all tokens that can have reference in this step
            if (override.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            // Construct raw typography, gradient, shadow and border colors
            if (!tokenTypeIsPure(override.type) &&
                override.type !== TokenType.color) {
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                const token = this.constructThemedValueToken(override, themeId);
                this.resolvedOverrides.set(token.id, token);
            }
        }
        /*
         * Step 7: Create all remaining tokens, as all pure tokens that can be references and all value tokens with possible mixins were all resolved.
         */
        for (const override of overrideData) {
            // Skip creation of all tokens that are not references
            if (!override.data.aliasTo) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line no-continue
                continue;
            }
            if (!this.resolvedOverrides.get(override.tokenPersistentId)) {
                // We create the token only when it wasn't created already. In some cases, this can happen when there are multiple reference levels
                // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
                const token = this.constructReferencedThemedToken(override, themeId);
                this.resolvedOverrides.set(token.id, token);
            }
        }
        /*
         * Step 8: Assign parents to the tree
         */
        for (const tokenGroup of tokenGroups) {
            for (const childId of tokenGroup.childrenIds) {
                const possibleToken = this.resolvedOverrides.get(childId);
                if (possibleToken) {
                    // If object exists, assign parent to it. Not existing means it is a group
                    possibleToken.parentGroupId = tokenGroup.id;
                }
            }
        }
        /**
         * Step 9: Assign sourceType to origin to differenciate Figma from Tokens Studio tokens
         */
        // Assign sourceType to origin to differenciate Figma from Tokens Studio tokens
        for (const token of overrideData.filter((t) => { var _a; return ((_a = t.origin) === null || _a === void 0 ? void 0 : _a.sourceId) && !t.origin.sourceType; })) {
            const source = sources.find((s) => { var _a; return s.id === ((_a = token.origin) === null || _a === void 0 ? void 0 : _a.sourceId); });
            if (source && source.type !== SourceType.storybook && token.origin) {
                token.origin.sourceType = source.type;
            }
        }
        /*
         * Step 10: Create all remaining tokens, as all pure tokens that can be references and all value tokens with possible mixins were all resolved.
         */
        const theme = new TokenTheme(data, this.versionId);
        const themeOverrides = Array.from(this.resolvedOverrides.values());
        this.fixMultilayerShadowTokens(themeOverrides.filter((t) => t.tokenType === TokenType.shadow), tokenGroups);
        this.fixMultilayerGradientTokens(themeOverrides.filter((t) => t.tokenType === TokenType.gradient), tokenGroups);
        this.fixMultilayerBlurTokens(themeOverrides.filter((t) => t.tokenType === TokenType.blur), tokenGroups);
        theme.overriddenTokens = themeOverrides;
        return theme;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token replication
    makeThemedValuelessTokenReplica(token, themeId, origin) {
        const replica = ThemeUtilities.replicateTokenAsThemePrefabWithoutValue(token, themeId, origin, this.versionId);
        replica.themeId = themeId;
        return replica;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Temporary: Data structure manipulation
    fixMultilayerShadowTokens(tokens, groups) {
        for (const token of tokens) {
            if (token.shadowLayers.length === 0) {
                token.shadowLayers = this.findAssociatedTokens(token, tokens, groups);
            }
        }
    }
    fixMultilayerGradientTokens(tokens, groups) {
        for (const token of tokens) {
            token.gradientLayers = this.findAssociatedTokens(token, tokens, groups);
        }
    }
    fixMultilayerBlurTokens(tokens, groups) {
        for (const token of tokens) {
            token.blurLayers = this.findAssociatedTokens(token, tokens, groups);
        }
    }
    findAssociatedTokens(token, tokens, groups) {
        var _a;
        if (!((_a = token.origin) === null || _a === void 0 ? void 0 : _a.id) || !token.parentGroupId) {
            return [token];
        }
        // Find all tokens with same origin
        const tokenId = this.actualOriginTokenId(token.origin.id);
        const matchingTokens = tokens.filter(
        // @ts-expect-error TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
        (t) => { var _a; return this.actualOriginTokenId((_a = t.origin) === null || _a === void 0 ? void 0 : _a.id) === tokenId; });
        // Sort by the parent, and set virtual tokens
        const sortedTokens = new Array();
        let index = 0;
        const parent = groups.find((g) => g.id === token.parentGroupId);
        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
        for (const id of parent.tokenIds) {
            for (const matchingToken of matchingTokens) {
                if (matchingToken.id === id) {
                    sortedTokens.push(matchingToken);
                    if (index === 0) {
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        ;
                        matchingToken.isVirtual = false;
                    }
                    else {
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        ;
                        matchingToken.isVirtual = true;
                    }
                    index += 1;
                }
            }
        }
        return sortedTokens;
    }
    actualOriginTokenId(id) {
        if (id && id.length > 0) {
            return id.split(",")[0];
        }
        return null;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Referencing
    constructReferencedToken(rawData, properties, values, path = new Set()) {
        const referenceId = rawData.data.aliasTo;
        let referencedToken;
        // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        if (this.resolvedTokens.get(referenceId)) {
            // If the referenced token already exists, we use it to create this token
            // @ts-expect-error TS(2322): Type 'Token | undefined' is not assignable to type... Remove this comment to see the full error message
            referencedToken = this.resolvedTokens.get(referenceId);
            // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        }
        else if (this.hashedTokens.get(referenceId)) {
            if (path.has(rawData.persistentId)) {
                // handle the circular reference
                // @ts-expect-error TS(2322): Type 'Token | undefined' is not assignable to type... Remove this comment to see the full error message
                referencedToken = this.resolvedTokens.get(referenceId);
            }
            else {
                // Otherwise, we request token with the model data of the other token coming from the base set of tokens
                referencedToken = this.constructReferencedToken(
                // @ts-expect-error TS(2345): Argument of type 'TokenRemoteModel | undefined' is... Remove this comment to see the full error message
                this.hashedTokens.get(referenceId), properties, values, path.add(rawData.persistentId));
            }
        }
        else {
            // This situation can't happen because we are either resolving base tokens and using constructed references,
            // or resolving theme and this function should not be used at all
            throw new Error(`Data integrity problem detected, can't start creating reference for themed tokens when resolving base values. ` +
                `Token id ${rawData.id}, reference id: ${referenceId}`);
        }
        const token = this.constructResolvedToken(rawData, referencedToken, properties, values);
        this.resolvedTokens.set(token.id, token);
        return token;
    }
    constructReferencedThemedToken(rawData, themeId, path = new Set()) {
        const referenceId = rawData.data.aliasTo;
        let referencedToken;
        // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        if (this.resolvedOverrides.get(referenceId)) {
            // If the reference token exists from the current theme that is being resolved (note, that might not be always), prioritize reference first
            // @ts-expect-error TS(2322): Type 'Token | undefined' is not assignable to type... Remove this comment to see the full error message
            referencedToken = this.resolvedOverrides.get(referenceId);
            // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        }
        else if (this.hashedOverrides.get(referenceId)) {
            if (path.has(rawData.tokenPersistentId)) {
                // Handle the circular reference
                // @ts-expect-error TS(2345): Argument of type 'TokenThemeOverrideRemoteModel | ... Remove this comment to see the full error message
                referencedToken = this.resolvedTokens.get(referenceId);
            }
            else {
                // If the reference doesn't exists, there are two possible options:
                // The first one is that we are creating reference token within current theme, if provided.
                // In this case, we construct token from the override that we also construct
                referencedToken = this.constructReferencedThemedToken(
                // @ts-expect-error TS(2345): Argument of type 'TokenThemeOverrideRemoteModel | ... Remove this comment to see the full error message
                this.hashedOverrides.get(referenceId), themeId, path.add(rawData.tokenPersistentId));
            }
            // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        }
        else if (this.resolvedTokens.get(referenceId)) {
            // If the referenced token already exists, we use it to create this token
            // @ts-expect-error TS(2322): Type 'Token | undefined' is not assignable to type... Remove this comment to see the full error message
            referencedToken = this.resolvedTokens.get(referenceId);
        }
        else {
            // This situation can't happen because we are either resolving theme and using constructed references,
            // or resolving base tokens and this function should not be used at all
            throw new Error("Data integrity problem detected, can't start creating reference for base tokens when resolving themes");
        }
        const token = this.constructResolvedThemedToken(rawData, referencedToken, themeId);
        this.resolvedOverrides.set(token.id, token);
        return token;
    }
    constructResolvedThemedToken(rawData, referencedToken, themeId) {
        if (!rawData.data.aliasTo) {
            throw Error(`Incorrectly creating reference themed token ${rawData.tokenPersistentId} from pure value token`);
        }
        const baseToken = this.resolvedTokens.get(rawData.tokenPersistentId);
        const replica = ThemeUtilities.replicateTokenAsThemePrefabWithoutValue(
        // @ts-expect-error TS(2345): Argument of type 'Token | undefined' is not assign... Remove this comment to see the full error message
        baseToken, themeId, rawData.origin, this.versionId);
        // Each token has some value, let's use it
        replica.value = referencedToken.value;
        if (!Array.isArray(replica.value)) {
            replica.value = Object.assign({}, replica.value);
            replica.value.referencedTokenId = referencedToken.id;
        }
        else {
            // TODO:fix-sdk-eslint
            // @ts-expect-error TS(2322): Type '({ color: ColorTokenValue; x: number; y: num... Remove this comment to see the full error message
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            replica.value = replica.value.map((replicatedValue) => (Object.assign({}, replicatedValue)));
            // @ts-expect-error TS(2488): Type 'StringTokenValue | ProductCopyTokenValue | F... Remove this comment to see the full error message
            for (const v of replica.value) {
                // TODO:fix-sdk-eslint
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                v.referencedTokenId = referencedToken.id;
            }
        }
        return replica;
    }
    constructResolvedToken(rawData, referencedToken, properties, values) {
        if (!rawData.data.aliasTo) {
            throw Error("Incorrectly creating reference token from value token");
        }
        if (!referencedToken) {
            throw Error(`Data integrity problem detected, can't start creating reference for unexisting alias: ${rawData.id}, ${rawData.data}`);
        }
        const { type } = rawData;
        // We make a copy of values for each constructed token
        switch (type) {
            case TokenType.color: {
                const ref = referencedToken;
                return new ColorToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.border: {
                const ref = referencedToken;
                return new BorderToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.gradient: {
                const ref = referencedToken;
                return new GradientToken(this.versionId, rawData, ref.value, ref, properties, values);
            }
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            // eslint-disable-next-line no-fallthrough
            case TokenType.zIndex: {
                return this.constructResolvedDimensionToken(rawData, properties, values, type, referencedToken);
            }
            case TokenType.shadow: {
                const ref = referencedToken;
                return new ShadowToken(this.versionId, rawData, ref.value, ref, properties, values);
            }
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            // eslint-disable-next-line no-fallthrough
            case TokenType.fontWeight: {
                return this.constructResolvedStringToken(rawData, properties, values, type, referencedToken);
            }
            case TokenType.textCase:
            case TokenType.textDecoration:
            // eslint-disable-next-line no-fallthrough
            case TokenType.visibility: {
                return this.constructResolvedOptionToken(rawData, properties, values, type, referencedToken);
            }
            case TokenType.typography: {
                const ref = referencedToken;
                return new TypographyToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.blur: {
                const ref = referencedToken;
                return new BlurToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            default:
                throw new UnreachableCaseError(type);
        }
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token construction
    constructValueToken(rawData, properties, values) {
        if (rawData.data.aliasTo) {
            throw Error("Incorrectly creating value token from referenced token");
        }
        const { type } = rawData;
        switch (type) {
            case TokenType.color:
                return this.constructColorToken(rawData, properties, values);
            case TokenType.border:
                return this.constructBorderToken(rawData, properties, values);
            case TokenType.gradient:
                return this.constructGradientToken(rawData, properties, values);
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                return this.constructDimensionToken(rawData, properties, values, type);
            case TokenType.shadow:
                return this.constructShadowToken(rawData, properties, values);
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            case TokenType.fontWeight:
                return this.constructStringToken(rawData, properties, values, type);
            case TokenType.typography:
                return this.constructTypographyToken(rawData, properties, values);
            case TokenType.blur:
                return this.constructBlurToken(rawData, properties, values);
            case TokenType.textCase:
            case TokenType.textDecoration:
            case TokenType.visibility:
                return this.constructOptionToken(rawData, properties, values, type);
            default:
                throw new UnreachableCaseError(type);
        }
    }
    constructThemedValueToken(override, themeId) {
        if (override.data.aliasTo) {
            throw Error("Incorrectly creating themed value token from referenced token");
        }
        const baseToken = this.resolvedTokens.get(override.tokenPersistentId);
        const replica = ThemeUtilities.replicateTokenAsThemePrefabWithoutValue(
        // @ts-expect-error TS(2345): Argument of type 'Token | undefined' is not assign... Remove this comment to see the full error message
        baseToken, themeId, override.origin, this.versionId);
        const { type } = override;
        switch (type) {
            case TokenType.color:
                ;
                replica.value = this.constructColorValue(
                // @ts-expect-error TS(2345): Argument of type 'ColorTokenRemoteValue | undefine... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                this.constructDimensionValueForReplica(type, replica, override);
                break;
            case TokenType.string:
            case TokenType.productCopy:
            case TokenType.fontFamily:
            case TokenType.fontWeight:
                this.constructStringValueForReplica(type, replica, override);
                break;
            case TokenType.textCase:
            case TokenType.textDecoration:
            case TokenType.visibility:
                this.constructOptionValueForReplica(type, replica, override);
                break;
            case TokenType.blur:
                ;
                replica.value = this.constructBlurTokenValue(
                // @ts-expect-error TS(2345): Argument of type 'BlurTokenRemoteValue | undefined... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.border:
                ;
                replica.value = this.constructBorderTokenValue(
                // @ts-expect-error TS(2345): Argument of type 'BorderTokenRemoteValue | undefin... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.gradient:
                ;
                replica.value = this.constructGradientTokenValues(
                // @ts-expect-error TS(2345): Argument of type 'GradientTokenRemoteValue | Gradi... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.shadow:
                ;
                replica.value = this.constructShadowTokenValues(
                // @ts-expect-error TS(2345): Argument of type 'ShadowTokenRemoteValue | ShadowT... Remove this comment to see the full error message
                override.data.value);
                break;
            case TokenType.typography:
                ;
                replica.value =
                    this.constructTypographyTokenValue(
                    // @ts-expect-error TS(2345): Argument of type 'TypographyTokenRemoteValue | und... Remove this comment to see the full error message
                    override.data.value);
                break;
            default:
                throw new UnreachableCaseError(type);
        }
        return replica;
    }
    constructColorToken(rawData, properties, values) {
        // @ts-expect-error TS(2345): Argument of type 'ColorTokenRemoteValue | undefine... Remove this comment to see the full error message
        const value = this.constructColorValue(rawData.data.value);
        return new ColorToken(this.versionId, rawData, value, null, properties, values);
    }
    constructColorValue(rawValue) {
        if (typeof rawValue.color === "string") {
            const hex = rawValue.color.substr(1); // RRGGBBAA
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            const opacity = this.resolveReferencedDimensionTokenValue(rawValue.opacity, TokenType.opacity);
            return {
                color: {
                    r,
                    g,
                    b,
                    referencedTokenId: null,
                },
                opacity,
                referencedTokenId: null,
            };
        }
        // TEST: Limit depth
        const color = this.resolveReferencedColorTokenValue(rawValue.color);
        const opacity = this.resolveReferencedDimensionTokenValue(rawValue.opacity, TokenType.opacity);
        return {
            color: {
                r: color.color.r,
                g: color.color.g,
                b: color.color.b,
                // TEST: Proper resolution of color.
                // Maybe should set referencedTokenId above? Or here as one inside color.color.
                referencedTokenId: color.referencedTokenId,
            },
            opacity,
            referencedTokenId: null,
        };
    }
    constructResolvedStringToken(rawData, properties, values, tokenType, referencedToken) {
        switch (tokenType) {
            case TokenType.string: {
                const ref = referencedToken;
                return new StringToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.productCopy: {
                const ref = referencedToken;
                return new ProductCopyToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.fontFamily: {
                const ref = referencedToken;
                return new FontFamilyToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.fontWeight: {
                const ref = referencedToken;
                return new FontWeightToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            default:
                throw new UnreachableCaseError(tokenType);
        }
    }
    constructStringToken(rawData, properties, values, tokenType) {
        // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        const value = this.constructTextLikeTokenValue(rawData.data.value);
        switch (tokenType) {
            case TokenType.string:
                return new StringToken(this.versionId, rawData, value, null, properties, values);
            case TokenType.productCopy:
                return new ProductCopyToken(this.versionId, rawData, value, null, properties, values);
            case TokenType.fontFamily:
                return new FontFamilyToken(this.versionId, rawData, value, null, properties, values);
            case TokenType.fontWeight:
                return new FontWeightToken(this.versionId, rawData, value, null, properties, values);
            default:
                throw new UnreachableCaseError(tokenType);
        }
    }
    constructResolvedOptionToken(rawData, properties, values, tokenType, referencedToken) {
        switch (tokenType) {
            case TokenType.textCase: {
                const ref = referencedToken;
                return new TextCaseToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.textDecoration: {
                const ref = referencedToken;
                return new TextDecorationToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.visibility: {
                const ref = referencedToken;
                return new VisibilityToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            default:
                throw new UnreachableCaseError(tokenType);
        }
    }
    constructOptionToken(rawData, properties, values, tokenType) {
        // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        const value = this.constructOptionLikeTokenValue(rawData.data.value);
        switch (tokenType) {
            case TokenType.textCase:
                return new TextCaseToken(this.versionId, rawData, value, null, properties, values);
            case TokenType.textDecoration:
                return new TextDecorationToken(this.versionId, rawData, value, null, properties, values);
            case TokenType.visibility:
                return new VisibilityToken(this.versionId, rawData, value, null, properties, values);
            default:
                throw new UnreachableCaseError(tokenType);
        }
    }
    constructTextLikeTokenValue(rawData) {
        const value = {
            text: rawData,
            referencedTokenId: null,
        };
        return value;
    }
    constructOptionLikeTokenValue(rawData) {
        const value = {
            value: rawData,
            referencedTokenId: null,
        };
        return value;
    }
    constructResolvedDimensionToken(rawData, properties, values, tokenType, referencedToken) {
        switch (tokenType) {
            case TokenType.dimension: {
                const ref = referencedToken;
                return new DimensionToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.size: {
                const ref = referencedToken;
                return new SizeToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.space: {
                const ref = referencedToken;
                return new SpaceToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.opacity: {
                const ref = referencedToken;
                return new OpacityToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.fontSize: {
                const ref = referencedToken;
                return new FontSizeToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.lineHeight: {
                const ref = referencedToken;
                return new LineHeightToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.letterSpacing: {
                const ref = referencedToken;
                return new LetterSpacingToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.paragraphSpacing: {
                const ref = referencedToken;
                return new ParagraphSpacingToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.borderWidth: {
                const ref = referencedToken;
                return new BorderWidthToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.radius: {
                const ref = referencedToken;
                return new RadiusToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.duration: {
                const ref = referencedToken;
                return new DurationToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            case TokenType.zIndex: {
                const ref = referencedToken;
                return new ZIndexToken(this.versionId, rawData, Object.assign({}, ref.value), ref, properties, values);
            }
            default:
                throw new UnreachableCaseError(tokenType);
        }
    }
    // TODO: <T> for safer approach?
    constructDimensionToken(rawData, properties, values, tokenType) {
        switch (tokenType) {
            case TokenType.dimension: {
                const dimensionValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new DimensionToken(this.versionId, rawData, dimensionValue, null, properties, values);
            }
            case TokenType.size: {
                const sizeValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new SizeToken(this.versionId, rawData, sizeValue, null, properties, values);
            }
            case TokenType.space: {
                const spaceValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                // TODO: of nominal typing, so pass sizeValue do not compile
                return new SpaceToken(this.versionId, rawData, spaceValue, null, properties, values);
            }
            case TokenType.opacity: {
                const opacityValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new OpacityToken(this.versionId, rawData, opacityValue, null, properties, values);
            }
            case TokenType.fontSize: {
                const fontSizeValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new FontSizeToken(this.versionId, rawData, fontSizeValue, null, properties, values);
            }
            case TokenType.lineHeight: {
                const lineHeightValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new LineHeightToken(this.versionId, rawData, lineHeightValue, null, properties, values);
            }
            case TokenType.letterSpacing: {
                const letterSpacingValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new LetterSpacingToken(this.versionId, rawData, letterSpacingValue, null, properties, values);
            }
            case TokenType.paragraphSpacing: {
                const paragraphSpacingValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new ParagraphSpacingToken(this.versionId, rawData, paragraphSpacingValue, null, properties, values);
            }
            case TokenType.borderWidth: {
                const borderWidthValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new BorderWidthToken(this.versionId, rawData, borderWidthValue, null, properties, values);
            }
            case TokenType.radius: {
                const borderRadiusValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new RadiusToken(this.versionId, rawData, borderRadiusValue, null, properties, values);
            }
            case TokenType.duration: {
                const durationValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new DurationToken(this.versionId, rawData, durationValue, null, properties, values);
            }
            case TokenType.zIndex: {
                const zIndexValue = this.constructDimensionValue(
                // @ts-expect-error TS(2769): No overload matches this call.
                rawData.data.value, tokenType);
                return new ZIndexToken(this.versionId, rawData, zIndexValue, null, properties, values);
            }
            default:
                throw new UnreachableCaseError(tokenType);
        }
    }
    constructDimensionValue(rawData, 
    // TODO:fix-sdk-eslint
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    type) {
        return {
            unit: rawData.unit,
            measure: rawData.measure,
            referencedTokenId: null,
        };
    }
    constructGradientToken(rawData, properties, values) {
        // @ts-expect-error TS(2345): Argument of type 'GradientTokenRemoteValue | Gradi... Remove this comment to see the full error message
        const value = this.constructGradientTokenValues(rawData.data.value);
        return new GradientToken(this.versionId, rawData, value, null, properties, values);
    }
    constructGradientTokenValues(rawData) {
        if (!Array.isArray(rawData)) {
            return [this.constructGradientTokenValue(rawData)];
        }
        const tokens = [];
        for (const token of rawData) {
            // FIXME: Is it safe to assume no aliasTo is found? Probably good idea to have separate types for ref vs raw
            // if (token.aliasTo) {
            //   throw new Error(
            //     `Called constructShadowTokenValues for ${token.aliasTo}`
            //   )
            const value = this.resolveReferencedGradientTokenValue(token);
            const toAdd = Array.isArray(value) ? value : [value];
            tokens.push(...toAdd);
            // }
            // FIXME: Rethink whether we should go deep or not
            // let value = this.constructGradientTokenValues(token.value)
            // let toAdd = Array.isArray(value) ? value : [value]
            // tokens.push(...toAdd)
        }
        return tokens;
    }
    constructGradientTokenValue(rawData) {
        const value = {
            to: rawData.to,
            from: rawData.from,
            type: rawData.type,
            aspectRatio: rawData.aspectRatio,
            stops: this.constructGradientStops(rawData.stops),
            referencedTokenId: null,
        };
        return value;
    }
    constructGradientStops(rawData) {
        return rawData.map((stop) => {
            return {
                position: stop.position,
                color: this.resolveReferencedColorTokenValue(stop.color),
                referencedTokenId: null,
            };
        });
    }
    constructShadowToken(rawData, properties, values) {
        // @ts-expect-error TS(2345): Argument of type 'ShadowTokenRemoteValue | ShadowT... Remove this comment to see the full error message
        const value = this.constructShadowTokenValues(rawData.data.value);
        return new ShadowToken(this.versionId, rawData, value, null, properties, values);
    }
    constructShadowTokenValues(rawData) {
        if (!Array.isArray(rawData)) {
            return [this.constructShadowTokenValue(rawData)];
        }
        const tokens = [];
        for (const token of rawData) {
            // FIXME: Is it safe to assume no aliasTo is found? Probably good idea to have separate types for ref vs raw
            // if (token.aliasTo) {
            //   throw new Error(
            //     `Called constructShadowTokenValues for ${token.aliasTo}`
            //   )
            const value = this.resolveReferencedShadowTokenValue(token);
            const toAdd = Array.isArray(value) ? value : [value];
            tokens.push(...toAdd);
            // }
            // FIXME: Rethink whether we should go deep or not
            // let value = this.constructShadowTokenValues(token.value)
            // let toAdd = Array.isArray(value) ? value : [value]
            // tokens.push(...toAdd)
        }
        return tokens;
    }
    constructShadowTokenValue(rawData) {
        const value = {
            color: this.resolveReferencedColorTokenValue(rawData.color),
            x: rawData.x,
            y: rawData.y,
            radius: rawData.radius,
            spread: rawData.spread,
            opacity: rawData.opacity
                ? this.resolveReferencedDimensionTokenValue(rawData.opacity, TokenType.opacity)
                : undefined,
            type: rawData.type,
            referencedTokenId: null,
        };
        return value;
    }
    constructBorderToken(rawData, properties, values) {
        // @ts-expect-error TS(2345): Argument of type 'BorderTokenRemoteValue | undefin... Remove this comment to see the full error message
        const value = this.constructBorderTokenValue(rawData.data.value);
        return new BorderToken(this.versionId, rawData, value, null, properties, values);
    }
    constructBorderTokenValue(rawData) {
        const value = {
            color: this.resolveReferencedColorTokenValue(rawData.color),
            width: this.resolveReferencedDimensionTokenValue(rawData.width, TokenType.borderWidth),
            position: rawData.position,
            style: rawData.style,
            referencedTokenId: null,
        };
        return value;
    }
    constructTypographyToken(rawData, properties, values) {
        // @ts-expect-error TS(2345): Argument of type 'TypographyTokenRemoteValue | und... Remove this comment to see the full error message
        const value = this.constructTypographyTokenValue(rawData.data.value);
        return new TypographyToken(this.versionId, rawData, value, null, properties, values);
    }
    constructTypographyTokenValue(rawData) {
        const value = {
            fontFamily: this.resolveReferencedStringTokenValue(rawData.fontFamily),
            fontWeight: this.resolveReferencedStringTokenValue(rawData.fontWeight),
            fontSize: this.resolveReferencedDimensionTokenValue(rawData.fontSize, TokenType.fontSize),
            textDecoration: this.resolveReferencedOptionTokenValue(rawData.textDecoration),
            textCase: this.resolveReferencedOptionTokenValue(rawData.textCase),
            letterSpacing: this.resolveReferencedDimensionTokenValue(rawData.letterSpacing, TokenType.letterSpacing),
            lineHeight: rawData.lineHeight
                ? this.resolveReferencedDimensionTokenValue(rawData.lineHeight, TokenType.lineHeight)
                : null,
            paragraphIndent: this.resolveReferencedDimensionTokenValue(rawData.paragraphIndent, TokenType.paragraphSpacing),
            paragraphSpacing: this.resolveReferencedDimensionTokenValue(rawData.paragraphSpacing, TokenType.paragraphSpacing),
            referencedTokenId: null,
        };
        return value;
    }
    constructBlurToken(rawData, properties, values) {
        // @ts-expect-error TS(2345): Argument of type 'BlurTokenRemoteValue | undefined... Remove this comment to see the full error message
        const value = this.constructBlurTokenValue(rawData.data.value);
        return new BlurToken(this.versionId, rawData, value, null, properties, values);
    }
    constructBlurTokenValue(rawData) {
        const value = {
            type: rawData.type,
            radius: this.resolveReferencedDimensionTokenValue(rawData.radius, TokenType.dimension),
            referencedTokenId: null,
        };
        return value;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token value resolution
    /** Resolve token color value - meaning we are not creating new tokens, and require raw tokens to be already present */
    resolveReferencedColorTokenValue(token) {
        var _a;
        if (token.aliasTo) {
            const resolved = ((_a = this.resolvedOverrides.get(token.aliasTo)) !== null && _a !== void 0 ? _a : this.resolvedTokens.get(token.aliasTo));
            return Object.assign(Object.assign({}, resolved.value), { referencedTokenId: resolved.id });
        }
        // @ts-expect-error TS(2345): Argument of type 'ColorTokenRemoteValue | undefine... Remove this comment to see the full error message
        return this.constructColorValue(token.value);
    }
    /** Resolve token dimension value - meaning we are not creating new tokens, and require raw tokens to be already present */
    resolveReferencedBorderWidthTokenValue(token) {
        var _a;
        if (token.aliasTo) {
            const resolved = ((_a = this.resolvedOverrides.get(token.aliasTo)) !== null && _a !== void 0 ? _a : this.resolvedTokens.get(token.aliasTo));
            return Object.assign(Object.assign({}, resolved.value), { referencedTokenId: resolved.id });
        }
        // @ts-expect-error TS(2769): No overload matches this call.
        return this.constructDimensionValue(token.value, TokenType.borderWidth);
    }
    resolveReferencedDimensionTokenValue(token, type) {
        var _a;
        if (token.aliasTo) {
            const resolved = ((_a = this.resolvedOverrides.get(token.aliasTo)) !== null && _a !== void 0 ? _a : this.resolvedTokens.get(token.aliasTo));
            return Object.assign(Object.assign({}, resolved.value), { referencedTokenId: resolved.id });
        }
        // TODO:fix-sdk-eslint
        // @ts-expect-error TS(2769): No overload matches this call.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return this.constructDimensionValue(token.value, type);
    }
    resolveReferencedStringTokenValue(token) {
        var _a;
        if (token.aliasTo) {
            const resolved = ((_a = this.resolvedOverrides.get(token.aliasTo)) !== null && _a !== void 0 ? _a : this.resolvedTokens.get(token.aliasTo));
            return Object.assign(Object.assign({}, resolved.value), { referencedTokenId: resolved.id });
        }
        // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
        return this.constructTextLikeTokenValue(token.value);
    }
    resolveReferencedGradientTokenValue(token) {
        var _a;
        if (token.aliasTo) {
            const resolved = ((_a = this.resolvedOverrides.get(token.aliasTo)) !== null && _a !== void 0 ? _a : this.resolvedTokens.get(token.aliasTo));
            return Object.assign(Object.assign({}, resolved.value), { referencedTokenId: resolved.id });
        }
        // @ts-expect-error TS(2345): Argument of type 'GradientTokenRemoteValue | Gradi... Remove this comment to see the full error message
        return this.constructGradientTokenValues(token.value);
    }
    resolveReferencedShadowTokenValue(token) {
        var _a;
        if (token.aliasTo) {
            const resolved = ((_a = this.resolvedOverrides.get(token.aliasTo)) !== null && _a !== void 0 ? _a : this.resolvedTokens.get(token.aliasTo));
            return resolved.value.map((v) => (Object.assign(Object.assign({}, v), { referencedTokenId: resolved.id })));
        }
        // @ts-expect-error TS(2345): Argument of type 'ShadowTokenRemoteValue | ShadowT... Remove this comment to see the full error message
        return this.constructShadowTokenValues(token.value);
    }
    resolveReferencedOptionTokenValue(token) {
        var _a;
        if (token.aliasTo) {
            const resolved = ((_a = this.resolvedOverrides.get(token.aliasTo)) !== null && _a !== void 0 ? _a : this.resolvedTokens.get(token.aliasTo));
            return Object.assign(Object.assign({}, resolved.value), { referencedTokenId: resolved.id });
        }
        return {
            // @ts-expect-error TS(2322): Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
            value: token.value,
            referencedTokenId: null,
        };
    }
}
